import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faBox,
  faTruck,
  faMapMarkerAlt,
  faCreditCard,
  faShieldAlt,
  faDownload,
  faArrowLeft,
  faSpinner,
  faTimesCircle,
  faExclamationCircle,
  faUser,
  faGift
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ordersAPI, userAPI, paymentAPI, productsAPI } from '../services/api';
import { useNotifications } from '../context/NotificationContext';
import EscrowStatus from './EscrowStatus';
import DeliveryTracking from './DeliveryTracking';

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { showCustomToast } = useNotifications();
  const paymentSuccess = searchParams.get('payment') === 'success';
  const paymentReference = searchParams.get('reference') || searchParams.get('paymentReference');

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState('buyer');
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  // Verify payment if payment=success and reference is present. Escrow
  // (Pandascrow) orders are confirmed entirely by webhook — this reference
  // is a Pandascrow transaction ref, and /payment/verify/:reference only
  // knows how to check Monnify, so it always 400s for one ("Could not find
  // transaction...") and threw a false "Verification Failed" toast even
  // though the escrow payment had genuinely succeeded. Skip it for escrow
  // orders; EscrowStatus already shows the real, webhook-driven status.
  useEffect(() => {
    if (paymentSuccess && paymentReference && !verifyingPayment && order && !order.use_escrow) {
      verifyPayment();
    }
  }, [paymentSuccess, paymentReference, order]);

  const verifyPayment = async () => {
    try {
      setVerifyingPayment(true);

      await paymentAPI.verifyPayment(paymentReference);

      showCustomToast({
        type: 'success',
        title: 'Payment Verified',
        message: 'Your payment has been confirmed successfully!'
      });

      // Refresh order details after payment verification
      await fetchOrderDetails();
    } catch (err) {
      showCustomToast({
        type: 'error',
        title: 'Verification Failed',
        message: err.message || 'Unable to verify payment. Please contact support.'
      });
    } finally {
      setVerifyingPayment(false);
    }
  };

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);

      const response = await ordersAPI.getById(orderId);

      if (response && response.data) {
        const orderData = response.data.order || response.data;

        // Fetch current user profile to get buyer information and determine role
        try {
          const userResponse = await userAPI.getProfile();

          if (userResponse && userResponse.data) {
            const user = userResponse.data.user || userResponse.data;
            const userId = user._id || user.id;

            // Store current user data for use as buyer info
            setCurrentUser(user);

            // Check if current user is the seller
            const sellerId = orderData.seller_id || orderData.seller?._id || orderData.seller?.id;
            if (sellerId === userId) {
              setUserRole('seller');
            } else {
              setUserRole('buyer');
            }
          }
        } catch (err) {
          setUserRole('buyer');
        }

        // Fetch product details for each item to get seller information
        if (orderData.items && orderData.items.length > 0) {
          try {
            const enrichedItems = await Promise.all(
              orderData.items.map(async (item) => {
                try {
                  const productId = item.product_id || item.product?._id || item.product?.id;

                  if (productId && typeof productId === 'string') {
                    const productResponse = await productsAPI.getById(productId);

                    if (productResponse && productResponse.data) {
                      const productData = productResponse.data.product || productResponse.data;

                      // Return enriched item with product data
                      return {
                        ...item,
                        product: productData,
                        seller: productData.seller,
                        seller_id: productData.seller_id || productData.seller?._id || productData.seller?.id,
                        seller_name: productData.seller_name || productData.seller?.name || productData.seller?.username
                      };
                    }
                  }

                  // Return original item if fetch fails
                  return item;
                } catch (productErr) {
                  return item;
                }
              })
            );

            // Update order with enriched items
            orderData.items = enrichedItems;
          } catch (enrichErr) {
            // Silently handle enrichment errors
          }
        }

        setOrder(orderData);
      } else {
        setError('Order not found');
      }
    } catch (err) {
      setError(err.message || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      confirmed: '#3b82f6',
      processing: '#8b5cf6',
      shipped: '#06b6d4',
      delivered: '#22c55e',
      cancelled: '#ef4444',
      refunded: '#6b7280'
    };
    return colors[status?.toLowerCase()] || '#9ca3af';
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return faSpinner;
      case 'confirmed':
      case 'processing':
        return faBox;
      case 'shipped':
        return faTruck;
      case 'delivered':
        return faCheckCircle;
      case 'cancelled':
        return faTimesCircle;
      case 'refunded':
        return faExclamationCircle;
      default:
        return faBox;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(price || 0);
  };

  // The price this line was actually BOUGHT at. `item.product` is attached
  // by fetchOrderDetails from the live products collection, so
  // `item.product.price` is today's listing price — if the seller has
  // repriced since, that is not what the buyer paid. unit_price is the
  // figure captured on the order, so it has to win. (The item rows already
  // ordered it this way; calculateSubtotal didn't, so the invoice's subtotal
  // could disagree with the sum of the lines printed directly above it.)
  const lineUnitPrice = (item) => {
    const product = item.product_id || item.product || item;
    return item.unit_price || item.price || product?.price || product?.unit_price || item.product_price || 0;
  };

  const calculateSubtotal = () => {
    // subtotal_amount is what the backend actually charged against; prefer
    // it over re-deriving from line items wherever it exists.
    if (order?.subtotal_amount) return order.subtotal_amount;

    if (!order?.items || order.items.length === 0) return 0;

    return order.items.reduce(
      (sum, item) => sum + lineUnitPrice(item) * (item.quantity || 1),
      0
    );
  };

  // Returns the viewer to wherever they actually came from.
  //
  // This used to be a hardcoded navigate('/buyer-dashboard'), which sent a
  // seller to the buyer dashboard and dropped everyone on that dashboard's
  // default section rather than the Orders list they opened the invoice
  // from. Prefer real history (the entry behind this one is the list they
  // came from, with its own scroll position and tab state intact) and fall
  // back to the right dashboard, explicitly asking for the orders section —
  // the dashboards already honour location.state.activeSection.
  const goBackToOrders = () => {
    if (location.key !== 'default' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    const dashboard = userRole === 'seller' ? '/seller-dashboard' : '/buyer-dashboard';
    navigate(dashboard, { state: { activeSection: 'orders' } });
  };

  // Resolves the buyer's name/email/phone for "Customer Information" (both
  // the on-screen section and the downloaded PDF). order.buyer_name/
  // buyer_email/buyer_phone come from the backend's buyerLookupStages() — a
  // real lookup of the order's actual buyer, correct regardless of who's
  // viewing the page. currentUser is only a safe stand-in for the buyer when
  // the viewer IS the buyer (userRole === 'buyer'); using it unconditionally
  // used to mean a SELLER opening their own order's receipt saw their OWN
  // name/email/phone printed as the customer's, because currentUser is
  // always "whoever is logged in right now."
  const getBuyerDisplayInfo = () => {
    const selfFallback = userRole === 'buyer';
    const backendName = (order?.buyer_name || '').trim();

    const name = backendName ||
      (selfFallback && (
        currentUser?.name || currentUser?.full_name || currentUser?.fullName ||
        `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() ||
        currentUser?.username
      )) ||
      order?.shipping_address?.name || order?.customer_name || 'Customer';

    const email = order?.buyer_email ||
      (selfFallback && currentUser?.email) ||
      order?.shipping_address?.email || order?.customer_email || order?.email || 'Not Provided';

    const phone = order?.buyer_phone ||
      (selfFallback && (currentUser?.phone || currentUser?.phone_number || currentUser?.phoneNumber)) ||
      order?.shipping_address?.phone || order?.customer_phone || order?.phone || 'Not Provided';

    return { name, email, phone };
  };

  const handleDownloadPDF = async () => {
    if (!order) return;

    try {
      setDownloadingPDF(true);

      // Determine if payment is completed (MUST BE DECLARED FIRST)
      const paymentCompleted = order.payment_status?.toLowerCase() === 'paid' ||
                              order.payment_status?.toLowerCase() === 'completed' ||
                              order.payment_status?.toLowerCase() === 'success' ||
                              order.payment_status?.toLowerCase() === 'successful' ||
                              paymentSuccess;

      // Determine document type for messages
      const docType = paymentCompleted ? 'receipt' : 'invoice';

      showCustomToast({
        type: 'info',
        title: 'Generating PDF',
        message: `Please wait while we generate your ${docType}...`
      });

      // Create a temporary div for the invoice HTML
      const invoiceContainer = document.createElement('div');
      invoiceContainer.style.position = 'absolute';
      invoiceContainer.style.left = '-9999px';
      invoiceContainer.style.width = '800px';
      invoiceContainer.style.background = '#0F172A'; // Dark mode background
      invoiceContainer.style.padding = '40px';
      document.body.appendChild(invoiceContainer);

      // Same corrected field names as the on-screen summary — see the
      // comment there. The downloaded invoice was missing Logistics and
      // the Escrow Fee entirely while still printing the all-in TOTAL.
      const subtotal = order.subtotal_amount || calculateSubtotal();
      const shipping = order.shipping_amount || order.shipping_fee || order.shipping_cost || 0;
      const logisticsVat = order.tax_amount || order.tax || 0;
      const escrowFee = order.escrow_fee_amount || 0;
      const discount = order.discount_amount || 0;
      const total = order.total_amount || order.total ||
        (subtotal + shipping + logisticsVat + escrowFee - discount);

      const documentTitle = paymentCompleted ? 'RECEIPT' : 'INVOICE';

      // Extract buyer information — see getBuyerDisplayInfo for why this
      // can't just default to currentUser.
      const { name: buyerName, email: buyerEmail, phone: buyerPhone } = getBuyerDisplayInfo();

      // Create invoice HTML with dark mode styling
      invoiceContainer.innerHTML = `
        <div style="font-family: 'Geist', sans-serif; background: #0F172A; color: #E5E7EB; padding: 0; margin: 0;">
          <!-- Header -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 50px; border-bottom: 4px solid #22C55E; padding-bottom: 25px; background: linear-gradient(135deg, #1E293B 0%, #0F172A 100%); padding: 30px; margin: -40px -40px 40px -40px;">
            <div>
              <div style="font-size: 42px; font-weight: 900; color: #22C55E; letter-spacing: 2px; text-shadow: 0 0 20px rgba(34, 197, 94, 0.5);">AutoboyExpress</div>
              <div style="font-size: 14px; color: #9CA3AF; margin-top: 8px;">Your Trusted Auto Marketplace</div>
            </div>
            <div style="text-align: right;">
              <h2 style="color: #22C55E; margin: 0 0 15px 0; font-size: 36px; font-weight: 800; text-shadow: 0 0 15px rgba(34, 197, 94, 0.4);">${documentTitle}</h2>
              <p style="margin: 6px 0; font-size: 13px; color: #9CA3AF;"><strong style="color: #E5E7EB;">${documentTitle} #:</strong> ${order._id || order.id}</p>
              <p style="margin: 6px 0; font-size: 13px; color: #9CA3AF;"><strong style="color: #E5E7EB;">Date:</strong> ${formatDate(order.created_at || order.createdAt)}</p>
              <p style="margin: 10px 0 0 0;"><span style="background: ${getStatusColor(order.status)}; color: white; padding: 8px 16px; border-radius: 25px; font-weight: 700; font-size: 12px; text-transform: uppercase; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">${(order.status || 'pending').toUpperCase()}</span></p>
            </div>
          </div>

          <!-- Buyer and Shipping Info -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 40px;">
            <div style="background: #1E293B; padding: 25px; border-radius: 16px; border: 1px solid rgba(34, 197, 94, 0.2); box-shadow: 0 4px 16px rgba(0,0,0,0.3);">
              <h3 style="color: #22C55E; margin: 0 0 20px 0; font-size: 18px; font-weight: 700; border-bottom: 2px solid rgba(34, 197, 94, 0.3); padding-bottom: 12px;">BILL TO</h3>
              <p style="margin: 10px 0; font-size: 14px; line-height: 1.8;"><strong style="color: #F9FAFB; font-weight: 600;">Name:</strong> <span style="color: #E5E7EB;">${buyerName}</span></p>
              ${userRole === 'seller' ? '' : `
              <p style="margin: 10px 0; font-size: 14px; line-height: 1.8;"><strong style="color: #F9FAFB; font-weight: 600;">Email:</strong> <span style="color: #E5E7EB;">${buyerEmail}</span></p>
              <p style="margin: 10px 0; font-size: 14px; line-height: 1.8;"><strong style="color: #F9FAFB; font-weight: 600;">Phone:</strong> <span style="color: #E5E7EB;">${buyerPhone}</span></p>`}
            </div>
            <div style="background: #1E293B; padding: 25px; border-radius: 16px; border: 1px solid rgba(34, 197, 94, 0.2); box-shadow: 0 4px 16px rgba(0,0,0,0.3);">
              <h3 style="color: #22C55E; margin: 0 0 20px 0; font-size: 18px; font-weight: 700; border-bottom: 2px solid rgba(34, 197, 94, 0.3); padding-bottom: 12px;">SHIP TO</h3>
              <p style="margin: 10px 0; font-size: 14px; line-height: 1.8;"><strong style="color: #F9FAFB; font-weight: 600;">Address:</strong> <span style="color: #E5E7EB;">${order.shipping_address?.street || order.shipping_address?.address || 'N/A'}</span></p>
              <p style="margin: 10px 0; font-size: 14px; line-height: 1.8;"><strong style="color: #F9FAFB; font-weight: 600;">City:</strong> <span style="color: #E5E7EB;">${order.shipping_address?.city || 'N/A'}</span></p>
              <p style="margin: 10px 0; font-size: 14px; line-height: 1.8;"><strong style="color: #F9FAFB; font-weight: 600;">State/ZIP:</strong> <span style="color: #E5E7EB;">${order.shipping_address?.state || 'N/A'}, ${order.shipping_address?.zip_code || order.shipping_address?.zipCode || 'N/A'}</span></p>
              <p style="margin: 10px 0; font-size: 14px; line-height: 1.8;"><strong style="color: #F9FAFB; font-weight: 600;">Country:</strong> <span style="color: #E5E7EB;">${order.shipping_address?.country || 'Nigeria'}</span></p>
            </div>
          </div>

          <!-- Order Items -->
          <div style="margin-bottom: 40px; background: #1E293B; padding: 25px; border-radius: 16px; border: 1px solid rgba(34, 197, 94, 0.2); box-shadow: 0 4px 16px rgba(0,0,0,0.3);">
            <h3 style="color: #22C55E; margin: 0 0 25px 0; font-size: 20px; font-weight: 700; border-bottom: 2px solid rgba(34, 197, 94, 0.3); padding-bottom: 12px;">ORDER ITEMS</h3>
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background: linear-gradient(135deg, #22C55E, #16A34A); color: white;">
                  <th style="padding: 15px 12px; text-align: left; font-weight: 700; font-size: 13px; border-top-left-radius: 8px;">ITEM</th>
                  <th style="padding: 15px 12px; text-align: left; font-weight: 700; font-size: 13px;">SELLER</th>
                  <th style="padding: 15px 12px; text-align: center; font-weight: 700; font-size: 13px;">QTY</th>
                  <th style="padding: 15px 12px; text-align: right; font-weight: 700; font-size: 13px;">UNIT PRICE</th>
                  <th style="padding: 15px 12px; text-align: right; font-weight: 700; font-size: 13px; border-top-right-radius: 8px;">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                ${(order.items || []).map((item, idx) => {
                  const product = item.product_id || item.product || item;
                  const productName = product?.title ||
                                    product?.name ||
                                    item.product_name ||
                                    item.name ||
                                    item.title ||
                                    item.product_title ||
                                    'Product';

                  // Shared with calculateSubtotal so the lines and the
                  // subtotal are always derived the same way.
                  const itemPrice = lineUnitPrice(item);

                  const itemQuantity = item.quantity || item.qty || 1;

                  // Seller identity masking: non-premium sellers show as "Seller XXX"
                  const isPremiumSeller = product?.seller_premium || item.seller_premium || order.seller_premium;
                  const rawSellerName = product?.seller_name ||
                                    product?.sellerName ||
                                    item.seller_name ||
                                    item.sellerName ||
                                    product?.seller?.name ||
                                    product?.seller?.full_name ||
                                    product?.seller?.fullName ||
                                    product?.seller?.username ||
                                    product?.seller?.store_name ||
                                    product?.seller?.storeName ||
                                    item.seller?.name ||
                                    item.seller?.full_name ||
                                    item.seller?.fullName ||
                                    item.seller?.username ||
                                    item.seller?.store_name ||
                                    item.seller?.storeName ||
                                    order.seller?.name ||
                                    order.seller?.full_name ||
                                    order.seller?.username ||
                                    order.seller?.store_name ||
                                    order.seller_name ||
                                    'Seller';
                  const sellerIdStr = product?.seller_id || item.seller_id || order.seller_id || '000';
                  const sellerName = isPremiumSeller
                    ? rawSellerName
                    : `Seller ${String(sellerIdStr).slice(-3).padStart(3, '0')}`;

                  return `
                    <tr style="background: ${idx % 2 === 0 ? '#111827' : '#1E293B'}; border-bottom: 1px solid rgba(255, 255, 255, 0.05);">
                      <td style="padding: 15px 12px; color: #F9FAFB; font-weight: 600; font-size: 13px;">${productName}</td>
                      <td style="padding: 15px 12px; color: #22C55E; font-size: 13px;">${sellerName}</td>
                      <td style="padding: 15px 12px; text-align: center; color: #E5E7EB; font-size: 13px; font-weight: 600;">${itemQuantity}</td>
                      <td style="padding: 15px 12px; text-align: right; color: #E5E7EB; font-size: 13px;">${formatPrice(itemPrice)}</td>
                      <td style="padding: 15px 12px; text-align: right; color: #22C55E; font-weight: 700; font-size: 14px;">${formatPrice(itemPrice * itemQuantity)}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>

          <!-- Order Summary -->
          <div style="background: #1E293B; padding: 25px; border-radius: 16px; border: 1px solid rgba(34, 197, 94, 0.2); box-shadow: 0 4px 16px rgba(0,0,0,0.3); margin-bottom: 40px;">
            <h3 style="color: #22C55E; margin: 0 0 25px 0; font-size: 20px; font-weight: 700; border-bottom: 2px solid rgba(34, 197, 94, 0.3); padding-bottom: 12px;">ORDER SUMMARY</h3>
            <div style="display: flex; justify-content: space-between; margin-bottom: 15px; padding: 12px; background: #111827; border-radius: 8px;">
              <div style="font-weight: 600; color: #E5E7EB; font-size: 15px;">Subtotal:</div>
              <div style="text-align: right; color: #22C55E; font-weight: 700; font-size: 15px;">${formatPrice(subtotal)}</div>
            </div>
            ${shipping > 0 ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 15px; padding: 12px; background: #111827; border-radius: 8px;">
                <div style="font-weight: 600; color: #E5E7EB; font-size: 15px;">Logistics:</div>
                <div style="text-align: right; color: #22C55E; font-weight: 700; font-size: 15px;">${formatPrice(shipping)}</div>
              </div>
            ` : ''}
            ${logisticsVat > 0 ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 15px; padding: 12px; background: #111827; border-radius: 8px;">
                <div style="font-weight: 600; color: #E5E7EB; font-size: 15px;">VAT (on logistics):</div>
                <div style="text-align: right; color: #22C55E; font-weight: 700; font-size: 15px;">${formatPrice(logisticsVat)}</div>
              </div>
            ` : ''}
            ${escrowFee > 0 ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 15px; padding: 12px; background: #111827; border-radius: 8px;">
                <div style="font-weight: 600; color: #E5E7EB; font-size: 15px;">Escrow Fee:</div>
                <div style="text-align: right; color: #22C55E; font-weight: 700; font-size: 15px;">${formatPrice(escrowFee)}</div>
              </div>
            ` : ''}
            ${discount > 0 ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 15px; padding: 12px; background: #111827; border-radius: 8px;">
                <div style="font-weight: 600; color: #E5E7EB; font-size: 15px;">Discount:</div>
                <div style="text-align: right; color: #22C55E; font-weight: 700; font-size: 15px;">-${formatPrice(discount)}</div>
              </div>
            ` : ''}
            <div style="display: flex; justify-content: space-between; font-size: 22px; color: #22C55E; border-top: 3px solid #22C55E; padding-top: 20px; margin-top: 20px; padding: 20px; background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(22, 163, 74, 0.1)); border-radius: 12px;">
              <div style="font-weight: 800; text-shadow: 0 0 10px rgba(34, 197, 94, 0.3);">TOTAL:</div>
              <div style="text-align: right; font-weight: 800; text-shadow: 0 0 10px rgba(34, 197, 94, 0.3);">${formatPrice(total)}</div>
            </div>
          </div>

          <!-- Payment Information -->
          <div style="background: #1E293B; padding: 25px; border-radius: 16px; border: 1px solid rgba(34, 197, 94, 0.2); box-shadow: 0 4px 16px rgba(0,0,0,0.3); margin-bottom: 40px;">
            <h3 style="color: #22C55E; margin: 0 0 20px 0; font-size: 18px; font-weight: 700; border-bottom: 2px solid rgba(34, 197, 94, 0.3); padding-bottom: 12px;">PAYMENT INFORMATION</h3>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
              <p style="margin: 0; font-size: 14px; line-height: 1.8;"><strong style="color: #F9FAFB; font-weight: 600;">Payment Method:</strong> <span style="color: #E5E7EB;">${order.payment_method || 'N/A'}</span></p>
              <p style="margin: 0; font-size: 14px; line-height: 1.8;"><strong style="color: #F9FAFB; font-weight: 600;">Payment Status:</strong> <span style="color: ${paymentCompleted ? '#22C55E' : '#F59E0B'}; font-weight: 700; text-transform: uppercase;">${paymentCompleted ? 'PAID' : (order.payment_status || 'Pending')}</span></p>
            </div>
            ${(order.use_escrow || order.escrow_enabled) ? `<p style="margin: 12px 0 0; font-size: 13px; color: #22C55E; font-weight: 700;">🔒 Protected by Escrow</p>` : ''}
          </div>

          <!-- Footer -->
          <div style="margin-top: 60px; text-align: center; color: #9CA3AF; border-top: 3px solid rgba(34, 197, 94, 0.3); padding-top: 30px; background: linear-gradient(135deg, #1E293B 0%, #0F172A 100%); padding: 40px; margin: 50px -40px -40px -40px; border-bottom-left-radius: 16px; border-bottom-right-radius: 16px;">
            <p style="margin: 0 0 15px 0; font-size: 18px; font-weight: 700; color: #22C55E;">Thank You for Your Purchase!</p>
            ${paymentCompleted ? `
              <p style="margin: 8px 0; font-size: 14px; color: #22C55E; font-weight: 600;">✓ Payment Successfully Received</p>
            ` : ''}
            <p style="margin: 8px 0; font-size: 14px; color: #E5E7EB;">AutoboyExpress - Your Trusted Auto Marketplace</p>
            <p style="margin: 8px 0; font-size: 13px; color: #9CA3AF;">For support and inquiries, contact us at <span style="color: #22C55E; font-weight: 600;">support@AutoboyExpress.com</span></p>
            <p style="margin: 15px 0 0 0; font-size: 12px; color: #6B7280;">This is an automated ${documentTitle.toLowerCase()}. Please keep it for your records.</p>
          </div>
        </div>
      `;

      // Generate PDF using html2canvas and jsPDF
      const canvas = await html2canvas(invoiceContainer, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download PDF with appropriate filename
      const pdfFilename = paymentCompleted ? `Receipt-${order._id || order.id}.pdf` : `Invoice-${order._id || order.id}.pdf`;
      pdf.save(pdfFilename);

      // Remove temporary container
      document.body.removeChild(invoiceContainer);

      showCustomToast({
        type: 'success',
        title: 'PDF Downloaded',
        message: `${documentTitle === 'RECEIPT' ? 'Receipt' : 'Invoice'} has been downloaded successfully!`
      });
    } catch (err) {
      showCustomToast({
        type: 'error',
        title: 'Download Failed',
        message: 'Failed to generate PDF. Please try again.'
      });
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Shared page shell — matches the gradient background AutoboyExpress uses
  // on this page, with responsive top padding so the fixed floating navbar
  // never sits on top of the content on smaller screens.
  const PAGE_WRAP = 'tw-min-h-screen tw-w-full tw-overflow-x-hidden tw-bg-gradient-to-br tw-from-zinc-50 tw-via-zinc-100 tw-to-zinc-50 dark:tw-from-slate-900 dark:tw-via-slate-800 dark:tw-to-slate-900 tw-pt-[90px] sm:tw-pt-[100px] md:tw-pt-[120px] tw-pb-8';

  // Loading Animation
  if (loading) {
    return (
      <div className={PAGE_WRAP}>
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-6 tw-min-h-[50vh] tw-text-center tw-px-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <FontAwesomeIcon icon={faSpinner} className="tw-text-5xl tw-text-green-500 tw-drop-shadow-[0_0_20px_rgba(34,197,94,0.4)]" size="3x" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="tw-text-lg tw-font-semibold tw-text-zinc-800 dark:tw-text-zinc-100 tw-m-0"
          >
            {verifyingPayment ? 'Verifying Payment...' : 'Loading order details...'}
          </motion.h2>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !order) {
    return (
      <div className={PAGE_WRAP}>
        <motion.div
          className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-5 tw-min-h-[50vh] tw-text-center tw-px-4"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
          >
            <FontAwesomeIcon icon={faTimesCircle} size="4x" style={{ color: '#ef4444' }} />
          </motion.div>
          <h2 className="tw-text-2xl tw-font-bold tw-text-zinc-900 dark:tw-text-zinc-50 tw-m-0">Order Not Found</h2>
          <p className="tw-text-zinc-500 dark:tw-text-zinc-400 tw-m-0 tw-max-w-md tw-break-words">{error || 'Unable to load order details'}</p>
          <button
            className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-px-7 tw-py-3.5 tw-font-bold tw-text-white tw-bg-gradient-to-br tw-from-green-500 tw-to-green-700 tw-shadow-[0_10px_30px_-8px_rgba(34,197,94,0.55)] tw-transition-transform hover:tw--translate-y-0.5 tw-border-0 tw-cursor-pointer"
            onClick={() => navigate('/shop')}
          >
            Continue Shopping
          </button>
        </motion.div>
      </div>
    );
  }

  // The backend's field names are subtotal_amount / shipping_amount /
  // tax_amount / escrow_fee_amount / discount_amount / total_amount. This
  // read shipping_fee|shipping_cost and tax|total — names that don't exist
  // on the order — so Shipping silently resolved to 0 and never rendered,
  // and the escrow fee wasn't read at all. Total came from total_amount,
  // which DOES include shipping and escrow, so the invoice showed a total
  // its own line items couldn't add up to. That's the "escrow calculation
  // is incorrect" report: the number was right, the breakdown was missing.
  const subtotal = order.subtotal_amount || calculateSubtotal();
  const shipping = order.shipping_amount || order.shipping_fee || order.shipping_cost || 0;
  const logisticsVat = order.tax_amount || order.tax || 0;
  const escrowFee = order.escrow_fee_amount || 0;
  const discount = order.discount_amount || 0;
  const total = order.total_amount || order.total ||
    (subtotal + shipping + logisticsVat + escrowFee - discount);

  // Check if payment is completed
  const isPaymentCompleted = order.payment_status?.toLowerCase() === 'paid' ||
                             order.payment_status?.toLowerCase() === 'completed' ||
                             order.payment_status?.toLowerCase() === 'success' ||
                             order.payment_status?.toLowerCase() === 'successful' ||
                             paymentSuccess;

  // Shared "info card" look reused by shipping address / payment info / order
  // summary — background tint, radius, border, all theme-aware.
  const INFO_CARD = 'tw-bg-gradient-to-br tw-from-zinc-50 tw-to-zinc-100 dark:tw-from-zinc-800 dark:tw-to-zinc-900 tw-p-5 sm:tw-p-7 tw-rounded-2xl tw-border tw-border-zinc-200/60 dark:tw-border-white/10';
  // Every section within the receipt card, separated by a hairline — except
  // the last, which drops its own border/margin so the card doesn't end on
  // a stray line.
  const SECTION = 'tw-mb-8 sm:tw-mb-10 tw-pb-6 sm:tw-pb-8 tw-border-b tw-border-zinc-200/60 dark:tw-border-white/10 last:tw-border-b-0 last:tw-mb-0 last:tw-pb-0';
  const SECTION_H3 = 'tw-text-lg sm:tw-text-xl tw-font-bold tw-text-zinc-800 dark:tw-text-zinc-50 tw-m-0 tw-mb-5 sm:tw-mb-7 tw-flex tw-items-center tw-gap-3 tw-tracking-tight';

  return (
    <div className={PAGE_WRAP}>
      <div className="tw-max-w-[900px] tw-mx-auto tw-px-4 tw-relative tw-z-[1]">
        {/* Success Banner with Animation */}
        <AnimatePresence>
          {paymentSuccess && (
            <motion.div
              className="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-gap-5 sm:tw-gap-8 tw-bg-gradient-to-br tw-from-green-500 tw-to-green-700 tw-text-white tw-p-6 sm:tw-p-10 tw-rounded-[20px] tw-mb-8 tw-text-center sm:tw-text-left tw-shadow-[0_8px_32px_rgba(34,197,94,0.3)] tw-overflow-hidden"
              initial={{ opacity: 0, y: -50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -50, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 100 }}
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1], rotate: [0, 360] }}
                transition={{ duration: 0.6 }}
                className="tw-flex-shrink-0"
              >
                <FontAwesomeIcon icon={faCheckCircle} className="tw-text-5xl sm:tw-text-6xl tw-drop-shadow-[0_4px_8px_rgba(0,0,0,0.2)]" />
              </motion.div>
              <div className="tw-min-w-0">
                <h1 className="tw-text-2xl sm:tw-text-3xl tw-font-extrabold tw-m-0 tw-mb-2 tw-tracking-tight">Order Placed Successfully!</h1>
                <p className="tw-m-0 tw-text-base tw-opacity-95 tw-leading-relaxed">Thank you for your purchase. Your order has been received and is being processed.</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Order Details Card */}
        <motion.div
          className="tw-bg-white dark:tw-bg-slate-800/95 tw-rounded-[20px] tw-p-5 sm:tw-p-8 lg:tw-p-10 tw-shadow-[0_4px_24px_rgba(0,0,0,0.08)] dark:tw-shadow-[0_4px_24px_rgba(0,0,0,0.4)] tw-mb-8 tw-mt-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-start tw-gap-4 sm:tw-gap-6 tw-pb-6 sm:tw-pb-8 tw-border-b-2 tw-border-zinc-200 dark:tw-border-white/10 tw-mb-8">
            <div className="tw-min-w-0 tw-w-full sm:tw-w-auto">
              <h2 className="tw-text-xl sm:tw-text-2xl tw-font-extrabold tw-m-0 tw-mb-2 tw-tracking-tight tw-bg-gradient-to-br tw-from-zinc-800 tw-to-green-500 dark:tw-from-zinc-50 dark:tw-to-green-500 tw-bg-clip-text tw-text-transparent tw-break-words">
                {isPaymentCompleted ? 'Payment Receipt' : 'Order Details'}
              </h2>
              <p className="tw-text-sm sm:tw-text-base tw-text-zinc-500 dark:tw-text-zinc-400 tw-m-0 tw-my-1 tw-font-mono tw-font-semibold tw-tracking-wide tw-break-all">
                {isPaymentCompleted ? 'Receipt' : 'Order'} #{order._id || order.id}
              </p>
              <p className="tw-text-sm tw-text-zinc-400 dark:tw-text-zinc-500 tw-m-0 tw-mt-1">Placed on {formatDate(order.created_at || order.createdAt)}</p>
              {isPaymentCompleted && (
                <p className="tw-text-sm tw-text-green-500 tw-font-semibold tw-m-0 tw-mt-1">
                  ✓ Payment Confirmed
                </p>
              )}
            </div>
            <motion.div
              className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-px-5 sm:tw-px-7 tw-py-3 sm:tw-py-3.5 tw-rounded-xl tw-font-bold tw-text-base tw-border-2 tw-border-transparent tw-flex-shrink-0 tw-w-full sm:tw-w-auto"
              style={{ background: `${getStatusColor(order.status)}20`, color: getStatusColor(order.status) }}
              whileHover={{ scale: 1.05 }}
              animate={order.status?.toLowerCase() === 'pending' ? {
                scale: [1, 1.05, 1]
              } : {}}
              transition={{ duration: 1, repeat: order.status?.toLowerCase() === 'pending' ? Infinity : 0 }}
            >
              <FontAwesomeIcon icon={getStatusIcon(order.status)} spin={order.status?.toLowerCase() === 'pending'} className="tw-text-xl tw-drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" />
              <span>{order.status || 'Pending'}</span>
            </motion.div>
          </div>

          {order.special_packaging && (
            <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[0.78rem] tw-font-bold tw-text-amber-600 dark:tw-text-amber-400 tw-bg-amber-500/10 tw-border tw-border-amber-500/25 tw-rounded-full tw-px-3 tw-py-1.5 tw-mt-3">
              <FontAwesomeIcon icon={faGift} /> Premium Packaging
            </div>
          )}

          {/* Order Items */}
          <motion.div
            className={SECTION}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className={SECTION_H3}><FontAwesomeIcon icon={faBox} className="tw-text-green-500 tw-text-xl" /> Order Items</h3>
            <div className="tw-flex tw-flex-col tw-gap-5">
              {order.items && order.items.length > 0 ? (
                order.items.map((item, index) => {
                  // Extract product data from various possible structures
                  const product = item.product_id || item.product || item;
                  const productName = product?.title ||
                                     product?.name ||
                                     item.product_name ||
                                     item.name ||
                                     item.title ||
                                     item.product_title ||
                                     'Product';

                  // Shared with calculateSubtotal so the lines and the
                  // subtotal are always derived the same way.
                  const itemPrice = lineUnitPrice(item);

                  const itemQuantity = item.quantity || item.qty || 1;

                  // Handle image - could be array or string
                  let productImage = 'https://via.placeholder.com/90';
                  if (product?.images && Array.isArray(product.images) && product.images.length > 0) {
                    productImage = product.images[0];
                  } else if (product?.image) {
                    productImage = product.image;
                  } else if (item.product_image || item.image) {
                    productImage = item.product_image || item.image;
                  }

                  // Seller identity masking: non-premium sellers show as "Seller XXX"
                  const isPremiumSeller = product?.seller_premium || item.seller_premium || order.seller_premium;
                  const rawSellerName = product?.seller_name ||
                                    product?.sellerName ||
                                    item.seller_name ||
                                    item.sellerName ||
                                    product?.seller?.name ||
                                    product?.seller?.full_name ||
                                    product?.seller?.fullName ||
                                    product?.seller?.username ||
                                    product?.seller?.store_name ||
                                    product?.seller?.storeName ||
                                    item.seller?.name ||
                                    item.seller?.full_name ||
                                    item.seller?.fullName ||
                                    item.seller?.username ||
                                    item.seller?.store_name ||
                                    item.seller?.storeName ||
                                    order.seller?.name ||
                                    order.seller?.full_name ||
                                    order.seller?.username ||
                                    order.seller?.store_name ||
                                    order.seller_name ||
                                    'Unknown Seller';
                  const sellerIdStr = product?.seller_id || item.seller_id || order.seller_id || '000';
                  const sellerName = isPremiumSeller
                    ? rawSellerName
                    : `Seller ${String(sellerIdStr).slice(-3).padStart(3, '0')}`;

                  const { name: buyerName } = getBuyerDisplayInfo();

                  return (
                    <motion.div
                      key={index}
                      className="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-gap-4 sm:tw-gap-6 tw-p-4 sm:tw-p-6 tw-bg-gradient-to-br tw-from-zinc-50 tw-to-zinc-100 dark:tw-from-zinc-800 dark:tw-to-zinc-900 tw-rounded-2xl tw-border tw-border-zinc-200/60 dark:tw-border-white/10 tw-text-center sm:tw-text-left"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <img
                        src={productImage}
                        alt={productName}
                        className="tw-w-full sm:tw-w-[90px] tw-h-[200px] sm:tw-h-[90px] tw-object-cover tw-rounded-xl tw-flex-shrink-0 tw-shadow-[0_4px_12px_rgba(0,0,0,0.1)]"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/90?text=Product';
                        }}
                      />
                      {/* min-w-0 is the actual overflow fix: a flex-1 child
                          otherwise refuses to shrink below its content's
                          intrinsic width (a long product/seller name, or an
                          email with no natural break point), pushing the row
                          — and the whole card — wider than the viewport. */}
                      <div className="tw-flex-1 tw-min-w-0 tw-w-full">
                        <h4 className="tw-m-0 tw-mb-2 tw-text-base tw-font-bold tw-text-zinc-800 dark:tw-text-zinc-50 tw-leading-snug tw-break-words">{productName}</h4>
                        <p className="tw-text-green-500 tw-font-semibold tw-mb-2 tw-text-sm tw-break-words">
                          <i className="fas fa-store tw-mr-2"></i>
                          Sold by: {sellerName}
                        </p>
                        <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-zinc-500 dark:tw-text-zinc-400">Quantity: {itemQuantity}</p>
                        <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-zinc-500 dark:tw-text-zinc-400">Unit Price: {formatPrice(itemPrice)}</p>
                        <p className="tw-text-[0.85rem] tw-text-zinc-400 dark:tw-text-zinc-500 tw-mt-2 tw-break-words">
                          <i className="fas fa-user tw-mr-2"></i>
                          Buyer: {buyerName}
                        </p>
                      </div>
                      <div className="tw-text-xl tw-font-extrabold tw-text-green-500 tw-flex-shrink-0 tw-tabular-nums">
                        {formatPrice(itemPrice * itemQuantity)}
                      </div>
                    </motion.div>
                  );
                })
              ) : (
                <p className="tw-text-zinc-500 dark:tw-text-zinc-400">No items found</p>
              )}
            </div>
          </motion.div>

          {/* Customer Information (Bill To) */}
          <motion.div
            className={SECTION}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h3 className={SECTION_H3}><FontAwesomeIcon icon={faUser} className="tw-text-green-500 tw-text-xl" /> Customer Information</h3>
            <div className={`${INFO_CARD} tw-space-y-3`}>
              {(() => {
                const { name: customerName, email: customerEmail, phone: customerPhone } = getBuyerDisplayInfo();
                const ROW = 'tw-m-0 tw-text-zinc-600 dark:tw-text-zinc-400 tw-leading-relaxed tw-break-words';
                // Platform rule: personal contact details aren't shared
                // between buyer and seller. The buyer sees their own email
                // and phone on their copy; the seller sees the buyer's NAME
                // only — email was never actually gated here before, only
                // phone was, which is exactly the leak item 2 reports.
                const isSeller = userRole === 'seller';
                return (
                  <>
                    <p className={ROW}><strong className="tw-text-green-500">Name:</strong> {customerName}</p>
                    {!isSeller && (
                      <>
                        <p className={ROW}><strong className="tw-text-green-500">Email:</strong> {customerEmail}</p>
                        <p className={ROW}><strong className="tw-text-green-500">Phone:</strong> {customerPhone}</p>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </motion.div>

          {/* Shipping Address */}
          <motion.div
            className={SECTION}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h3 className={SECTION_H3}><FontAwesomeIcon icon={faMapMarkerAlt} className="tw-text-green-500 tw-text-xl" /> Shipping Address</h3>
            <div className={`${INFO_CARD} tw-space-y-3`}>
              {order.shipping_address ? (
                <>
                  <p className="tw-m-0 tw-text-zinc-800 dark:tw-text-zinc-50 tw-font-bold tw-break-words">
                    {order.shipping_address.first_name || order.shipping_address.firstName || ''} {order.shipping_address.last_name || order.shipping_address.lastName || ''}
                  </p>
                  <p className="tw-m-0 tw-text-zinc-600 dark:tw-text-zinc-400 tw-leading-relaxed tw-break-words">{order.shipping_address.street || order.shipping_address.address || 'No address provided'}</p>
                  <p className="tw-m-0 tw-text-zinc-600 dark:tw-text-zinc-400 tw-leading-relaxed tw-break-words">
                    {order.shipping_address.city || ''}{order.shipping_address.city && ', '}
                    {order.shipping_address.state || ''} {order.shipping_address.zip_code || order.shipping_address.zipCode || ''}
                  </p>
                  <p className="tw-m-0 tw-text-zinc-600 dark:tw-text-zinc-400 tw-leading-relaxed">{order.shipping_address.country || 'Nigeria'}</p>
                  {/* Same privacy rule as Customer Information above — the
                      street/city/state/country stay (a seller needs the
                      shipping destination to fulfil the order), but the
                      buyer's own phone/email don't belong on the seller's
                      copy of this page. */}
                  {userRole !== 'seller' && order.shipping_address.phone && <p className="tw-m-0 tw-text-zinc-600 dark:tw-text-zinc-400 tw-leading-relaxed tw-break-words">Phone: {order.shipping_address.phone}</p>}
                  {userRole !== 'seller' && order.shipping_address.email && <p className="tw-m-0 tw-text-zinc-600 dark:tw-text-zinc-400 tw-leading-relaxed tw-break-words">Email: {order.shipping_address.email}</p>}
                </>
              ) : (
                <p className="tw-m-0 tw-text-zinc-500 dark:tw-text-zinc-400">No shipping address provided</p>
              )}
            </div>
          </motion.div>

          {/* Payment Information */}
          <motion.div
            className={SECTION}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h3 className={SECTION_H3}><FontAwesomeIcon icon={faCreditCard} className="tw-text-green-500 tw-text-xl" /> Payment Information</h3>
            <div className={INFO_CARD}>
              <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-2 tw-my-4 first:tw-mt-0 tw-text-zinc-600 dark:tw-text-zinc-400">
                <span>Payment Method:</span>
                <strong className="tw-text-zinc-800 dark:tw-text-zinc-50 tw-font-bold tw-capitalize">{order.payment_method || 'N/A'}</strong>
              </div>
              <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-2 tw-my-4 last:tw-mb-0 tw-text-zinc-600 dark:tw-text-zinc-400">
                <span>Payment Status:</span>
                <strong className={`tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-bold tw-uppercase tw-tracking-wide ${isPaymentCompleted ? 'tw-bg-green-500/15 tw-text-green-600 dark:tw-text-green-400' : 'tw-bg-amber-500/15 tw-text-amber-600 dark:tw-text-amber-400'}`}>
                  {isPaymentCompleted ? 'Paid' : (order.payment_status || 'Pending')}
                </strong>
              </div>
              {(order.use_escrow || order.escrow_enabled) && (
                <motion.div className="tw-inline-flex tw-items-center tw-gap-3 tw-bg-amber-500/15 tw-text-amber-700 dark:tw-text-amber-400 tw-px-5 tw-py-3 tw-rounded-xl tw-font-bold tw-mt-4" whileHover={{ scale: 1.05 }}>
                  <FontAwesomeIcon icon={faShieldAlt} />
                  <span>Protected by Escrow</span>
                </motion.div>
              )}
            </div>
          </motion.div>

          {(order.use_escrow || order.escrow_enabled) && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
              {/* Was hardcoded to "buyer" — once a seller could reach this
                  page too (see the GetOrder fix), that silently showed them
                  the buyer's own "Confirm Delivery" OTP-release control on
                  their own sale. Pass the role actually detected above. */}
              <EscrowStatus orderId={order._id || order.id} userRole={userRole} onStatusChange={fetchOrderDetails} />
            </motion.div>
          )}

          {/* Delivery Tracking */}
          {order.tracking_id && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85 }}
            >
              <DeliveryTracking
                orderId={order._id || order.id}
                trackingId={order.tracking_id}
              />
            </motion.div>
          )}

          {/* Order Summary */}
          <motion.div
            className={`${SECTION} ${INFO_CARD}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <h3 className="tw-text-lg sm:tw-text-xl tw-font-bold tw-text-zinc-800 dark:tw-text-zinc-50 tw-m-0 tw-mb-5">Order Summary</h3>
            <div className="tw-flex tw-flex-col tw-gap-4">
              <div className="tw-flex tw-justify-between tw-items-center tw-text-base tw-text-zinc-600 dark:tw-text-zinc-400 tw-gap-3">
                <span className="tw-font-semibold">Subtotal:</span>
                <span className="tw-font-bold tw-font-mono tw-tabular-nums">{formatPrice(subtotal)}</span>
              </div>
              {shipping > 0 && (
                <div className="tw-flex tw-justify-between tw-items-center tw-text-base tw-text-zinc-600 dark:tw-text-zinc-400 tw-gap-3">
                  <span className="tw-font-semibold">Logistics:</span>
                  <span className="tw-font-bold tw-font-mono tw-tabular-nums">{formatPrice(shipping)}</span>
                </div>
              )}
              {/* VAT is charged on the logistics fee only — never on the
                  product subtotal. Labelled as such so it isn't read as a
                  general sales tax (it matches Cart/Checkout's wording). */}
              {logisticsVat > 0 && (
                <div className="tw-flex tw-justify-between tw-items-center tw-text-base tw-text-zinc-600 dark:tw-text-zinc-400 tw-gap-3">
                  <span className="tw-font-semibold">VAT (on logistics):</span>
                  <span className="tw-font-bold tw-font-mono tw-tabular-nums">{formatPrice(logisticsVat)}</span>
                </div>
              )}
              {escrowFee > 0 && (
                <div className="tw-flex tw-justify-between tw-items-center tw-text-base tw-text-zinc-600 dark:tw-text-zinc-400 tw-gap-3">
                  <span className="tw-font-semibold tw-inline-flex tw-items-center tw-gap-2">
                    <FontAwesomeIcon icon={faShieldAlt} className="tw-text-green-500" /> Escrow Fee:
                  </span>
                  <span className="tw-font-bold tw-font-mono tw-tabular-nums">{formatPrice(escrowFee)}</span>
                </div>
              )}
              {discount > 0 && (
                <div className="tw-flex tw-justify-between tw-items-center tw-text-base tw-text-green-600 dark:tw-text-green-400 tw-gap-3">
                  <span className="tw-font-semibold">Discount:</span>
                  <span className="tw-font-bold tw-font-mono tw-tabular-nums">−{formatPrice(discount)}</span>
                </div>
              )}
              <div className="tw-h-0.5 tw-bg-gradient-to-r tw-from-transparent tw-via-zinc-300 dark:tw-via-white/10 tw-to-transparent tw-my-1 tw-rounded-full"></div>
              <motion.div
                className="tw-flex tw-justify-between tw-items-center tw-text-xl sm:tw-text-2xl tw-font-extrabold tw-text-green-500 tw-pt-2 tw-tracking-tight tw-gap-3"
                whileHover={{ scale: 1.02 }}
              >
                {/* This figure is what the BUYER paid — logistics, its VAT,
                    and the escrow fee are all buyer-side charges, none of
                    which are the seller's money. A seller viewing their own
                    sale's invoice with no other figure on the page could
                    only read this large green "Total" as what they're
                    getting — which is the literal "seller receives ₦1M+ for
                    a product listed at ₦950k" report. Relabelled for the
                    seller, and "Your Payout" below is the real number. */}
                <span>{userRole === 'seller' ? 'Order Total (Paid by Buyer):' : 'Total:'}</span>
                <span className="tw-font-mono tw-tabular-nums tw-break-all tw-text-right">{formatPrice(total)}</span>
              </motion.div>
              {userRole === 'seller' && typeof order.seller_payout === 'number' && (
                <div className="tw-flex tw-justify-between tw-items-center tw-gap-3 tw-mt-1 tw-p-3 tw-rounded-xl tw-bg-green-500/10 tw-border tw-border-green-500/25">
                  <span className="tw-font-bold tw-text-green-700 dark:tw-text-green-400">
                    Your Payout
                    <span className="tw-block tw-text-xs tw-font-normal tw-text-zinc-500 dark:tw-text-zinc-400 tw-mt-0.5">
                      Product price − platform fee − VAT. Logistics and the escrow fee are the buyer's charges, not yours.
                    </span>
                  </span>
                  <span className="tw-font-mono tw-tabular-nums tw-text-xl tw-font-extrabold tw-text-green-700 dark:tw-text-green-400 tw-flex-shrink-0">
                    {formatPrice(order.seller_payout)}
                  </span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            className="tw-flex tw-flex-col sm:tw-flex-row tw-flex-wrap tw-gap-4 tw-mt-8 sm:tw-mt-10 tw-pt-8 sm:tw-pt-10 tw-border-t-2 tw-border-zinc-200/60 dark:tw-border-white/10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
          >
            <motion.button
              className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-6 tw-py-3.5 tw-rounded-2xl tw-font-bold tw-text-zinc-600 dark:tw-text-zinc-200 tw-bg-white dark:tw-bg-slate-800 tw-border-2 tw-border-zinc-200 dark:tw-border-white/10 tw-w-full sm:tw-w-auto hover:tw-border-green-500 hover:tw-text-green-500 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              onClick={goBackToOrders}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Orders
            </motion.button>
            <motion.button
              className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-6 tw-py-3.5 tw-rounded-2xl tw-font-bold tw-text-zinc-600 dark:tw-text-zinc-200 tw-bg-white dark:tw-bg-slate-800 tw-border-2 tw-border-zinc-200 dark:tw-border-white/10 tw-w-full sm:tw-w-auto hover:tw-border-green-500 hover:tw-text-green-500 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              onClick={handleDownloadPDF}
              disabled={downloadingPDF}
              whileHover={{ scale: downloadingPDF ? 1 : 1.05 }}
              whileTap={{ scale: downloadingPDF ? 1 : 0.95 }}
            >
              <FontAwesomeIcon icon={downloadingPDF ? faSpinner : faDownload} spin={downloadingPDF} />
              {downloadingPDF ? 'Generating...' : `Download ${isPaymentCompleted ? 'Receipt' : 'Invoice'}`}
            </motion.button>
            <motion.button
              className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-6 tw-py-3.5 tw-rounded-2xl tw-font-bold tw-text-white tw-bg-gradient-to-br tw-from-green-500 tw-to-green-700 tw-shadow-[0_4px_16px_rgba(34,197,94,0.3)] tw-flex-1 tw-w-full sm:tw-w-auto hover:tw--translate-y-0.5 tw-transition-transform"
              onClick={() => navigate('/shop')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Continue Shopping
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Tracking Timeline */}
        {(order.status === 'shipped' || order.status === 'delivered') && (
          <motion.div
            className="tw-bg-white dark:tw-bg-slate-800/95 tw-rounded-[20px] tw-p-5 sm:tw-p-8 lg:tw-p-10 tw-shadow-[0_4px_24px_rgba(0,0,0,0.08)] dark:tw-shadow-[0_4px_24px_rgba(0,0,0,0.4)]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <h3 className="tw-text-xl sm:tw-text-2xl tw-font-extrabold tw-text-zinc-800 dark:tw-text-zinc-50 tw-m-0 tw-mb-8 sm:tw-mb-10 tw-flex tw-items-center tw-gap-3">
              <FontAwesomeIcon icon={faTruck} className="tw-text-green-500" /> Order Tracking
            </h3>
            <div className="tw-relative tw-pl-8 sm:tw-pl-10">
              <div className="tw-absolute tw-left-2.5 tw-top-0 tw-bottom-0 tw-w-[3px] tw-bg-gradient-to-b tw-from-zinc-200 tw-to-zinc-200/30 dark:tw-from-white/10 dark:tw-to-white/[0.03] tw-rounded-full" />
              <motion.div
                className="tw-relative tw-pb-8 sm:tw-pb-10"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1 }}
              >
                <div className="tw-absolute tw--left-[1.6rem] tw-top-1 tw-w-5 tw-h-5 tw-rounded-full tw-bg-gradient-to-br tw-from-green-500 tw-to-green-700 tw-border-4 tw-border-white dark:tw-border-slate-800 tw-shadow-[0_0_0_3px_rgba(34,197,94,0.3),0_0_20px_rgba(34,197,94,0.4)] tw-scale-110 tw-z-[1]"></div>
                <div className="tw-min-w-0">
                  <h4 className="tw-m-0 tw-mb-2 tw-text-base tw-font-extrabold tw-text-green-500">Order Confirmed</h4>
                  <p className="tw-m-0 tw-text-sm tw-text-zinc-500 dark:tw-text-zinc-400">{formatDate(order.created_at || order.createdAt)}</p>
                </div>
              </motion.div>
              <motion.div
                className="tw-relative tw-pb-8 sm:tw-pb-10"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2 }}
              >
                <div className="tw-absolute tw--left-[1.6rem] tw-top-1 tw-w-5 tw-h-5 tw-rounded-full tw-bg-gradient-to-br tw-from-green-500 tw-to-green-700 tw-border-4 tw-border-white dark:tw-border-slate-800 tw-shadow-[0_0_0_3px_rgba(34,197,94,0.3),0_0_20px_rgba(34,197,94,0.4)] tw-scale-110 tw-z-[1]"></div>
                <div className="tw-min-w-0">
                  <h4 className="tw-m-0 tw-mb-2 tw-text-base tw-font-extrabold tw-text-green-500">Processing</h4>
                  <p className="tw-m-0 tw-text-sm tw-text-zinc-500 dark:tw-text-zinc-400">{formatDate(order.updated_at || order.updatedAt)}</p>
                </div>
              </motion.div>
              <motion.div
                className="tw-relative tw-pb-8 sm:tw-pb-10"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.3 }}
              >
                <div className={`tw-absolute tw--left-[1.6rem] tw-top-1 tw-w-5 tw-h-5 tw-rounded-full tw-border-4 tw-border-white dark:tw-border-slate-800 tw-z-[1] ${
                  order.status === 'shipped' || order.status === 'delivered'
                    ? 'tw-bg-gradient-to-br tw-from-green-500 tw-to-green-700 tw-shadow-[0_0_0_3px_rgba(34,197,94,0.3),0_0_20px_rgba(34,197,94,0.4)] tw-scale-110'
                    : 'tw-bg-zinc-200 dark:tw-bg-zinc-600 tw-shadow-[0_0_0_3px_#E5E7EB] dark:tw-shadow-[0_0_0_3px_rgba(75,85,99,0.3)]'
                }`}></div>
                <div className="tw-min-w-0">
                  <h4 className={`tw-m-0 tw-mb-2 tw-text-base tw-font-bold ${order.status === 'shipped' || order.status === 'delivered' ? 'tw-text-green-500 tw-font-extrabold' : 'tw-text-zinc-800 dark:tw-text-zinc-50'}`}>Shipped</h4>
                  <p className="tw-m-0 tw-text-sm tw-text-zinc-500 dark:tw-text-zinc-400">{order.shipped_at ? formatDate(order.shipped_at) : 'In progress...'}</p>
                </div>
              </motion.div>
              <motion.div
                className="tw-relative"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.4 }}
              >
                <div className={`tw-absolute tw--left-[1.6rem] tw-top-1 tw-w-5 tw-h-5 tw-rounded-full tw-border-4 tw-border-white dark:tw-border-slate-800 tw-z-[1] ${
                  order.status === 'delivered'
                    ? 'tw-bg-gradient-to-br tw-from-green-500 tw-to-green-700 tw-shadow-[0_0_0_3px_rgba(34,197,94,0.3),0_0_20px_rgba(34,197,94,0.4)] tw-scale-110'
                    : 'tw-bg-zinc-200 dark:tw-bg-zinc-600 tw-shadow-[0_0_0_3px_#E5E7EB] dark:tw-shadow-[0_0_0_3px_rgba(75,85,99,0.3)]'
                }`}></div>
                <div className="tw-min-w-0">
                  <h4 className={`tw-m-0 tw-mb-2 tw-text-base tw-font-bold ${order.status === 'delivered' ? 'tw-text-green-500 tw-font-extrabold' : 'tw-text-zinc-800 dark:tw-text-zinc-50'}`}>Delivered</h4>
                  <p className="tw-m-0 tw-text-sm tw-text-zinc-500 dark:tw-text-zinc-400">{order.delivered_at ? formatDate(order.delivered_at) : 'Pending...'}</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default OrderConfirmation;
